import React, { useEffect, useRef } from "react";

/**
 * Detail dialog for a single service.
 * Closes on backdrop click and Escape; locks page scroll while open.
 */
export default function ServiceModal({ service, onClose }) {
  const closeRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      // Keep tabbing inside the dialog
      if (e.key === "Tab" && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll(
          'a[href], button, iframe, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    closeRef.current?.focus();

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!service) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-ink-950/85 p-4 backdrop-blur-md sm:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="service-modal-title"
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        className="relative my-auto w-full max-w-5xl border border-white/10 bg-ink-900 shadow-2xl"
        style={{ animation: "modal-in .4s cubic-bezier(.16,1,.3,1) both" }}
      >
        {/* Hazard rule across the top */}
        <div className="h-1 bg-hazard opacity-80" />

        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-5 z-10 flex h-10 w-10 items-center justify-center border border-white/15 bg-ink-950/80 text-xl text-steel-300 transition-colors hover:border-flame-500 hover:text-flame-500"
        >
          ×
        </button>

        <div className="grid gap-0 lg:grid-cols-12">
          {/* Visual + specs */}
          <div className="relative lg:col-span-5">
            <div className="relative aspect-[4/3] overflow-hidden lg:aspect-auto lg:h-full lg:min-h-[24rem]">
              <img
                src={service.image}
                alt={service.title}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/20 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-ink-900" />
            </div>

            {service.specs?.length > 0 && (
              <dl className="absolute inset-x-0 bottom-0 flex flex-wrap gap-x-8 gap-y-3 p-6">
                {service.specs.map((spec) => (
                  <div key={spec.label}>
                    <dt className="font-mono text-[10px] uppercase tracking-wide2 text-steel-400">
                      {spec.label}
                    </dt>
                    <dd className="h-display text-xl text-flame-500">
                      {spec.value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>

          {/* Copy */}
          <div className="max-h-[70vh] overflow-y-auto p-7 sm:p-10 lg:col-span-7">
            <p className="kicker mb-4">
              {service.num} — {service.tagline}
            </p>
            <h2
              id="service-modal-title"
              className="h-display text-4xl sm:text-5xl"
            >
              {service.title}
            </h2>

            {service.video && (
              <div className="mt-7 aspect-video w-full border border-white/10">
                <iframe
                  src={service.video}
                  title={`${service.title} — overview video`}
                  className="h-full w-full"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}

            {service.body?.map((para, i) => (
              <p key={i} className="mt-5 text-steel-300">
                {para}
              </p>
            ))}

            {service.sections?.map((section) => (
              <div key={section.heading} className="mt-9">
                <h3 className="mb-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-kicker text-white">
                  <span className="h-3 w-[2px] bg-flame-500" />
                  {section.heading}
                </h3>

                {section.body?.map((para, i) => (
                  <p key={i} className="mt-3 text-steel-300">
                    {para}
                  </p>
                ))}

                {section.items && (
                  <ul className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
                    {section.items.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-3 border-b border-white/5 py-2 text-sm text-steel-300"
                      >
                        <span className="mt-[7px] h-1 w-1 shrink-0 rotate-45 bg-flame-500" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            <div className="mt-10 border-t border-white/10 pt-7">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                  document
                    .getElementById("contact")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-flame group"
              >
                Enquire about this service
                <span className="transition-transform duration-300 group-hover:translate-x-1">
                  →
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: translateY(24px) scale(.985); }
          to   { opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
}
